"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Arrow } from "@/components/Arrow";

interface Props {
  children: React.ReactNode[];
  /** Auto-advance drift direction: 1 = rightwards, -1 = leftwards. */
  direction?: 1 | -1;
  autoAdvance?: boolean;
  intervalMs?: number;
}

export function TestimonialCarousel({
  children,
  direction = 1,
  autoAdvance = true,
  intervalMs = 5000,
}: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const pausedRef = useRef(false);

  const updateBounds = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateBounds();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateBounds, { passive: true });
    window.addEventListener("resize", updateBounds);
    return () => {
      el.removeEventListener("scroll", updateBounds);
      window.removeEventListener("resize", updateBounds);
    };
  }, [updateBounds]);

  const stepWidth = useCallback(() => {
    const el = trackRef.current;
    if (!el) return 0;
    const card = el.querySelector<HTMLElement>("[data-carousel-card]");
    return card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
  }, []);

  const scroll = useCallback(
    (dir: 1 | -1) => {
      const el = trackRef.current;
      if (!el) return;
      el.scrollBy({ left: stepWidth() * dir, behavior: "smooth" });
    },
    [stepWidth],
  );

  // Auto-advance: drift in `direction`, loop at the end, pause on hover/focus
  // or when the tab is hidden, and skip entirely under reduced-motion.
  useEffect(() => {
    if (!autoAdvance) return;
    const el = trackRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Drifting leftwards needs room to travel, so start at the far end.
    if (direction === -1) el.scrollLeft = el.scrollWidth;

    const pause = () => {
      pausedRef.current = true;
    };
    const resume = () => {
      pausedRef.current = false;
    };
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("focusin", pause);
    el.addEventListener("focusout", resume);

    const id = window.setInterval(() => {
      if (pausedRef.current || document.hidden) return;
      const atEnd =
        direction === 1
          ? el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
          : el.scrollLeft <= 4;
      if (atEnd) {
        el.scrollTo({ left: direction === 1 ? 0 : el.scrollWidth, behavior: "smooth" });
      } else {
        el.scrollBy({ left: stepWidth() * direction, behavior: "smooth" });
      }
    }, intervalMs);

    return () => {
      window.clearInterval(id);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
      el.removeEventListener("focusin", pause);
      el.removeEventListener("focusout", resume);
    };
  }, [autoAdvance, direction, intervalMs, stepWidth]);

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="-mx-5 flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-6 sm:px-6 lg:-mx-12 lg:px-12"
      >
        {children.map((child, i) => (
          <div
            key={i}
            data-carousel-card
            className="flex w-[85%] shrink-0 snap-start sm:w-[60%] lg:w-[31%]"
          >
            {child}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => scroll(-1)}
          disabled={!canPrev}
          aria-label="Previous reviews"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors hover:border-coral hover:text-coral disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-paper/30 disabled:hover:text-paper"
        >
          <Arrow className="rotate-180" />
        </button>
        <button
          type="button"
          onClick={() => scroll(1)}
          disabled={!canNext}
          aria-label="Next reviews"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors hover:border-coral hover:text-coral disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-paper/30 disabled:hover:text-paper"
        >
          <Arrow />
        </button>
      </div>
    </div>
  );
}
